//
//

import { mapActions, mapGetters } from "vuex";
import { debounce } from "quasar";
import AQInput from "@/components/Generics/AQInput/AQInput.vue";
import AQRadio from "@/components/Generics/AQRadio/AQRadio.vue";
import AQCheckbox from "@/components/Generics/AQCheckbox/AQCheckbox.vue";
import AQTooltip from "@/components/Generics/AQTooltip/AQTooltip.vue";
import AQButton from "@/components/Generics/AQButton/AQButton.vue";
import AQAvatar from "@/components/Generics/AQAvatar/AQAvatar.vue";

export default {
    name: "UserList",
    components: {
        AQInput,
        AQRadio,
        AQCheckbox,
        AQButton,
        AQAvatar,
        AQTooltip
    },

    props: {
        hasManager: {
            type: Object,
            default: () => {}
        },
        usersList: {
            type: Array,
            default: () => []
        },
        modalOpen: {
            type: Boolean,
            default: false
        },
        value: {
            type: Array,
            default: () => []
        },
        multiselect: {
            type: Boolean,
            default: false
        },
        fromProcessInspector: {
            type: Boolean,
            default: false
        },
        hasLoadMore: {
            type: Boolean,
            default: false
        },
        enablePagination: {
            type: Boolean,
            default: true
        },
        userListSearchValue: {
            type: String,
            default: ""
        },
        dataFromUrl: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            itemsToLoad: 20,
            userCheck: "",
            search: null,
            // eslint-disable-next-line global-require
            defaultAvatar: require("@/assets/images/user.svg"),
            selected: "",
            userCount: "",
            multipleUserCheck: [],
            loadDefaultElementsNr: 0,
            compCreated: false,
            showLoadMore: true,
            paginationSettings: { // used on pagination
                pageNumber: 0,
                pageSize: 50
            },
            itemsToShowOnPagination: [],
            containerInitialData: [],
            systemTypeList: ["TSYS", "TOAUTHUSR", "TANON"],
            remainingUsersToShow: null,
            noUsersAvailable: false
        };
    },
    computed: {
        ...mapGetters("admin", [
            "userList",
            "userDDL",
            "getCurrentUsers",
            "getUsersforEdit",
            "getCurrentGroupSelected",
            "roleList"
        ]),
        ...mapGetters("userProfile", ["userCookieInfo"]),
        filteredList() {
            let list = [];
            if (this.fromProcessInspector) {
                list = this.itemsToShowOnPagination.filter(user => !user.realmId);
            } else if (!this.usersList) {
                const filteredUsers = this.filtredUserList(this.userList);
                if (this.search && this.search.length >= 3) {
                    const lowerCaseValue = this.search.toLowerCase();
                    list = filteredUsers.filter(item => {
                        const fullName = `${item.firstName} ${item.lastName}`;
                        return fullName.toLowerCase().includes(lowerCaseValue) || item.email.toLowerCase().includes(lowerCaseValue);
                    });
                } else {
                    list = filteredUsers.sort((a, b) => {
                        const nameA = a.firstName ? a.firstName.toLowerCase() : a.firstName;
                        const nameB = b.firstName ? b.firstName.toLowerCase() : b.firstName;
                        if (nameA < nameB) return -1;
                        if (nameA > nameB) return 1;
                        return 0;
                    });
                }
            } else {
                const filteredUsers = this.filtredUserList(this.usersList);
                const lowerCaseValue = this.search ? this.search.toLowerCase().trim() : "";
                if (lowerCaseValue && lowerCaseValue.length >= 3) {
                    list = filteredUsers.filter(item => {
                        const fullName = item.name || (`${item.firstName} ${item.lastName}`);
                        return fullName.toLowerCase().includes(lowerCaseValue) || item.email.toLowerCase().includes(lowerCaseValue);
                    });
                } else {
                    list = filteredUsers;
                }
                const alreadyFiltered = [...list];
                list = alreadyFiltered.slice(0, this.itemsToLoad);
                if (list.length < 20 || (list.length >= alreadyFiltered.length)) {
                    this.$emit("hasLoadMore", false);
                } else {
                    this.$emit("hasLoadMore", true);
                }
            }
            return list;
        }
    },
    watch: {
        value: {
            handler(newValue) {
                if (newValue && !newValue.length && this.multiselect) {
                    this.multipleUserCheck = [];
                    if (this.fromProcessInspector) {
                        this.search = "";
                        this.searchUsers();
                    }
                } else if (newValue && newValue.length && this.multiselect && this.dataFromUrl) {
                    this.multipleUserCheck = newValue;
                }
            }
        },
        modalOpen: {
            handler(newValue) {
                if (newValue) {
                    if (this.hasManager) {
                        this.userCheck = this.hasManager.id;
                    } else {
                        this.userCheck = "";
                    }
                    this.$emit("userSelected", this.userCheck);
                } else {
                    this.selected = "";
                }
            }
        },
        userListSearchValue: {
            handler(newValue) {
                this.search = newValue;
                this.searchUsersList();
            }
        }
    },
    created() {
        if (this.fromProcessInspector) {
            this.searchUsersList = debounce(() => {
                if (this.enablePagination) {
                    this.itemsToShowOnPagination = [];
                    this.paginationSettings.pageNumber = 0;
                }
                this.getUsersData();
            }, 600);
            this.getUsersData();
        }
    },

    methods: {
        ...mapActions("admin", [
            "getUsersList",
            "createNewUser",
            "addUsersToRole",
            "getRoleList",
            "getUsersForRole"
        ]),
        setCustomAvatar(item) {
            return item && (item.avatarPath || item.avatar) ? `/identity/tenants/users/image/${item.id}/s` : null;
        },
        setUserType(user) {
            let type;
            if (user.type === "TOAUTHUSR") {
                type = "systemExternalUser";
            } else if (user.type === "TSYS") {
                type = "systemInternalUser";
            } else {
                type = (user.sourceType === "AD" || user.realmId
                    || user.type === "TSAMLUSR" || user.type === "TOPENIDUSR") ? "externalUser" : "userPlatform";
            }
            return type;
        },
        filtredUserList(data) {
            return data.filter(elem => !this.systemTypeList.includes(elem.type) && elem.status === "ACTIVE");
        },
        getUserName(user) {
            let name = "";
            if (this.fromProcessInspector && (user.type === "TOAUTHUSR" || user.type === "TSYS")) {
                name = user.firstName;
            } else {
                name = user.name || user.firstName || "";
                if (user.lastName) {
                    name = `${name} ${user.lastName}`.trim();
                }
            }
            return name;
        },
        getUserDescription(user) {
            let description = "";
            if (this.fromProcessInspector && user.type === "TOAUTHUSR") {
                description = (user.code ? `[${user.code}] ` : "") + user.lastName;
            } else if (this.fromProcessInspector && user.type === "TSYS") {
                description = this.$t("admin.usersAndRoles.addGroupMembers.systemUser");
            } else {
                description = user.email;
            }
            return description;
        },
        selectItem() {
            if (this.multipleUserCheck.length) {
                this.userCount = `${this.multipleUserCheck.length}
                 ${this.$t(`admin.usersAndRoles.addGroupMembers.${this.multipleUserCheck.length > 1 ? "showSelectedNrs" : "showSelectedNr"}`)}
                 ${this.$t("admin.usersAndRoles.addGroupMembers.showSelected")}`;
            } else {
                this.userCount = "";
            }
            this.$emit("input", this.multipleUserCheck);
        },
        selectUser(user) {
            this.$emit("userSelected", this.userCheck);
            this.$emit("setNewUser", user);
        },

        // different logic for users list from process inspector, the search is made in backend and there is pagination, because in PI there is a separate call
        getUsersData(loadMore) {
            this.loadDefaultElementsNr = 13;
            let payload = {
                searchValue: this.search || "",
                fromProcessInspector: this.fromProcessInspector,
                pagination: this.enablePagination
            };
            if (this.enablePagination) {
                payload = { ...payload, ...this.paginationSettings };
            }
            this.$store.dispatch("admin/getUsersList", payload).then(
                data => {
                    if (data && data.data.list.length) {
                        if (this.enablePagination) {
                            this.itemsToShowOnPagination = [...this.itemsToShowOnPagination, ...data.data.list];
                            if (data.data.list.length <= this.loadDefaultElementsNr) {
                                this.showLoadMore = false;
                            } else {
                                this.remainingUsersToShow = this.remainingUsersToShow
                                    ? (this.remainingUsersToShow - data.data.list.length)
                                    : (data.data.totalCount - data.data.list.length);
                                this.showLoadMore = this.remainingUsersToShow ? this.remainingUsersToShow > 0 : !!this.remainingUsersToShow;
                            }
                        } else if (this.containerInitialData && !this.containerInitialData.length) {
                            this.containerInitialData = data.data.list;
                        }
                        this.noUsersAvailable = false;
                    } else if (this.enablePagination && loadMore && this.search && this.itemsToShowOnPagination && this.itemsToShowOnPagination.length) {
                        this.noUsersAvailable = false;
                        this.showLoadMore = false;
                    } else {
                        this.noUsersAvailable = !!this.search;
                    }
                    setTimeout(() => {
                        this.loadDefaultElementsNr = 0;
                    }, 0);
                },
                // eslint-disable-next-line no-unused-vars
                error => {
                    this.showNotification(this.$t("admin.usersAndRoles.noUsersAvailable"));
                }

            );
        },
        showNotification(messageNotif) {
            this.$toast.showNotify({
                type: "error",
                message: messageNotif
            });
        },
        loadMoreItems() {
            if (this.hasLoadMore) {
                this.itemsToLoad += 20;
            } else {
                this.paginationSettings.pageNumber++;
                this.getUsersData(true);
            }
        },
        searchUsers() {
            const searchValueTrim = this.search ? this.search.trim() : "";
            if (searchValueTrim && searchValueTrim.length >= 3) {
                this.searchUsersList();
            } else {
                if (this.enablePagination) {
                    if (!searchValueTrim) {
                        this.search = "";
                        this.itemsToShowOnPagination = [];
                        this.paginationSettings.pageNumber = 0;
                        this.getUsersData();
                    }
                }
                this.noUsersAvailable = false;
            }
            this.$emit("searchValue", this.search);
        }
    }
};
